/**
 * Sound cues on the phone (MM #116).
 *
 * The wall has played cues since the beginning (AudioCueEngine in the Meteor
 * app); the phones only ever flashed. This is the client half.
 *
 * Three things make it non-obvious, and each shapes the design:
 *
 * 1. TIMING IS ALREADY SOLVED. Playback runs on a synced clock — TimeSync's
 *    median RTT offset, applied as `startTime - syncOffset` in PlaybackEngine.
 *    Cues therefore ride the SAME `t` the frames use, passed in per frame. We
 *    do not schedule against the audio hardware clock the way the wall does,
 *    because the wall plays one show on one machine and can pre-schedule the
 *    lot; a phone can join late, and its show start is a moving target until
 *    the countdown ends.
 *
 * 2. NOTHING MAY BE LOADED AT SHOWTIME. A clip fetched or decoded on first use
 *    arrives late, and on a 200ms cue late is the whole effect. Fetching and
 *    decoding are therefore SPLIT: prefetch() pulls the bytes as soon as the
 *    script is pulled — typically long before the countdown, and at whatever
 *    moment each phone happens to load, which spreads the requests instead of
 *    bunching them at showtime — while decodeAll() turns them into buffers once
 *    an AudioContext exists. Neither ever blocks the show; a clip that fails is
 *    simply silent.
 *
 * 3. AUDIO NEEDS A USER GESTURE — but only for DECODING and playback, not for
 *    the fetch. Mobile browsers start an AudioContext suspended, so unlock()
 *    must come from a real tap (the zone/row selector is the natural one). The
 *    split above means a phone that never taps has still paid the network cost
 *    up front, so sound starts the instant it does.
 *
 * Web Audio rather than <audio> elements because a show can fire the same clip
 * repeatedly in quick succession, and restarting one element cuts the previous
 * hit short. Buffers overlap naturally.
 */
import { buildCues, cuesInWindow } from '../sim/cues.js';

/**
 * Where the playhead sits before the first frame.
 *
 * NOT -1: the window is half-open (prev, t], and cuesInWindow drops any window
 * wider than its max jump to protect late joiners. Starting at -1 made the very
 * first frame a ~1s window, so a cue near t=0 was silently dropped — the show
 * opened in silence and nothing looked broken. A hair below zero keeps a cue at
 * exactly t=0 inside the first window while staying far too small to look like
 * a scrub. Cue times are rounded to 3 decimals, so this sits below any of them.
 */
const BEFORE_START = -0.001;

export class CueEngine {
  constructor() {
    this.ctx = null;
    this.bytes = new Map(); // key -> ArrayBuffer (fetched, not yet decoded)
    this.buffers = new Map(); // key -> AudioBuffer (decoded, ready to play)
    this.failed = new Set(); // keys we could not fetch/decode
    this.cues = [];
    this.muted = false;
    this.prevT = BEFORE_START;
    this.ready = false;
    this.fired = 0;
  }

  /** True when there is anything to play at all. */
  get hasCues() {
    return this.cues.length > 0;
  }

  /**
   * Build the cue list from the script. Pure and cheap — safe before any
   * gesture, and separate from preload() so we know whether audio is even
   * relevant for this show before asking for an AudioContext.
   */
  build(cueCode, { params = {}, zoneIndex = 0, zoneCount = 0, duration = 0 } = {}) {
    const { cues, error } = buildCues(cueCode, { params, zoneIndex, zoneCount, duration });
    if (error) console.warn('[Cues] cue code failed:', error);
    this.cues = cues;
    this.prevT = BEFORE_START;
    return cues.length;
  }

  /**
   * Create/resume the AudioContext. Must be called from a user gesture.
   * Safe to call repeatedly — later taps re-resume a context the browser
   * suspended when the page went to the background.
   */
  unlock() {
    try {
      if (!this.ctx) {
        const Ctor = window.AudioContext || window.webkitAudioContext;
        if (!Ctor) return false;
        this.ctx = new Ctor();
      }
      if (this.ctx.state === 'suspended') this.ctx.resume().catch(() => {});
      // Anything prefetched before the gesture can be decoded now.
      if (this.bytes.size) this.decodeAll().catch(() => {});
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Fetch the clip BYTES. Deliberately split from decoding.
   *
   * Clients pull the script long before the countdown, so the slow and highly
   * variable part — the network — should happen then, at whatever moment each
   * phone happens to load, spreading the requests out instead of bunching them
   * at showtime. Fetching needs no AudioContext and therefore no user gesture,
   * so it is never blocked waiting for a tap.
   *
   * Only clips a cue actually references are fetched, so a large shared clip
   * library costs the phone nothing.
   *
   * Never awaited by the show: a slow clip delays sound, never the lightshow.
   */
  async prefetch(audioClips = {}) {
    if (!this.cues.length) return { fetched: 0, failed: 0 };
    const needed = [...new Set(this.cues.map((c) => c.sound))];
    await Promise.all(
      needed.map(async (key) => {
        if (this.bytes.has(key) || this.buffers.has(key) || this.failed.has(key)) return;
        const url = audioClips?.[key];
        if (!url) {
          this.failed.add(key);
          console.warn(`[Cues] no URL for clip "${key}"`);
          return;
        }
        try {
          const res = await fetch(url, { mode: 'cors' });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          this.bytes.set(key, await res.arrayBuffer());
        } catch (err) {
          this.failed.add(key);
          console.warn(`[Cues] could not fetch "${key}":`, err.message);
        }
      })
    );
    // Decode straight away if a gesture already happened; otherwise this runs
    // from unlock().
    if (this.ctx) await this.decodeAll();
    return { fetched: this.bytes.size, failed: this.failed.size };
  }

  /**
   * Decode everything already fetched. Needs an AudioContext, so it runs after
   * unlock(). Cheap and CPU-local compared to the fetch.
   *
   * decodeAudioData DETACHES the ArrayBuffer it is given, so each entry is
   * dropped from `bytes` as it is consumed — decoding the same key twice would
   * otherwise throw on a detached buffer.
   */
  async decodeAll() {
    if (!this.ctx || !this.bytes.size) return this.buffers.size;
    const entries = [...this.bytes.entries()];
    this.bytes.clear();
    await Promise.all(
      entries.map(async ([key, buf]) => {
        if (this.buffers.has(key)) return;
        try {
          this.buffers.set(key, await this.ctx.decodeAudioData(buf));
        } catch (err) {
          this.failed.add(key);
          console.warn(`[Cues] could not decode "${key}":`, err.message);
        }
      })
    );
    this.ready = this.buffers.size > 0;
    return this.buffers.size;
  }

  /**
   * Fetch + decode in one call, for callers that already have a gesture.
   */
  async preload(audioClips = {}) {
    await this.prefetch(audioClips);
    if (this.ctx) await this.decodeAll();
    return { loaded: this.buffers.size, failed: this.failed.size };
  }

  /**
   * Advance to `tSeconds` and fire whatever the playhead just crossed.
   *
   * Called once per frame from PlaybackEngine with the same synced `t` the
   * script renders from, so audio and light cannot drift apart.
   */
  frame(tSeconds) {
    if (this.muted || !this.cues.length || !this.ctx) return;
    // Half-open (prev, next] so each cue fires exactly once, and nothing fires
    // at all if time jumped — a late joiner starting mid-show must not dump
    // every cue since t=0 at once.
    const due = cuesInWindow(this.cues, this.prevT, tSeconds);
    this.prevT = tSeconds;
    for (const cue of due) this._play(cue);
  }

  _play(cue) {
    const buffer = this.buffers.get(cue.sound);
    if (!buffer || !this.ctx) return; // missing clip: silent, never fatal
    try {
      const src = this.ctx.createBufferSource();
      const gain = this.ctx.createGain();
      gain.gain.value = Math.max(0, Math.min(cue.volume ?? 1, 1));
      src.buffer = buffer;
      src.connect(gain).connect(this.ctx.destination);
      src.start();
      this.fired++;
    } catch {
      // A phone that refuses to play must not take the lightshow down with it.
    }
  }

  setMuted(muted) {
    this.muted = Boolean(muted);
  }

  /** Rewind the playhead without dropping decoded buffers. */
  rewind() {
    this.prevT = BEFORE_START;
    this.fired = 0;
  }

  reset() {
    this.cues = [];
    this.bytes.clear();
    this.buffers.clear();
    this.failed.clear();
    this.prevT = BEFORE_START;
    this.fired = 0;
    this.ready = false;
  }
}
